import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { ScheduleRepository } from 'repository/scheduleRepository';
import { useBackDismiss } from 'hooks/useBackDismiss';
import { Button, Sheet } from 'components/ds';
import styles from './ScheduleHistorySection.module.css';

/**
 * 정기 모임 — 지난 회차(완료된 confirmed schedule) 이력 섹션.
 * - 미리보기 2건만 본문에 표시
 * - 3건 이상이면 "자세히" 버튼 → 모달에서 페이징 전체 열람
 */

interface Props {
    meetingId: string;
    /** 1페이지 당 표시 건수 (모달) */
    pageSize?: number;
}

interface ScheduleRow {
    scheduleId: string;
    startDatetime?: string;
    endDatetime?: string;
    isConfirmed?: boolean;
}

const DOW_KO = ['일', '월', '화', '수', '목', '금', '토'];

const formatRange = (startIso?: string, endIso?: string): string => {
    if (!startIso) return '';
    const s = new Date(startIso);
    const e = endIso ? new Date(endIso) : null;
    if (isNaN(s.getTime())) return '';
    const wd = i18n.t('common.weekDaysShort', { returnObjects: true });
    const wdArr = Array.isArray(wd) ? wd : DOW_KO;
    const dateText = `${s.getFullYear()}.${String(s.getMonth() + 1).padStart(2, '0')}.${String(s.getDate()).padStart(2, '0')} (${wdArr[s.getDay()]})`;
    const t1 = s.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    const t2 = e && !isNaN(e.getTime()) ? e.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) : '';
    return t2 ? `${dateText} · ${t1} ~ ${t2}` : `${dateText} · ${t1}`;
};

export const ScheduleHistorySection: React.FC<Props> = ({ meetingId, pageSize = 10 }) => {
    const { t } = useTranslation();
    const [all, setAll] = useState<ScheduleRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [page, setPage] = useState(1);

    useBackDismiss(showAll, () => setShowAll(false));

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const res: any = await ScheduleRepository.onGetSchedules(meetingId);
            const raw: any[] = res?.data?.data?.schedules || res?.data?.data || res?.data?.schedules || [];
            // 확정(is_confirmed=true) 이면서 종료된(end_datetime < now) 것만 이력으로
            const now = Date.now();
            const filtered: ScheduleRow[] = (Array.isArray(raw) ? raw : [])
                .filter((s: any) => {
                    if (!s) return false;
                    const confirmed = s.isConfirmed || s.is_confirmed;
                    if (!confirmed) return false;
                    const end = s.endDatetime || s.end_datetime;
                    if (!end) return false;
                    return new Date(end).getTime() < now;
                })
                .map((s: any) => ({
                    scheduleId: s.scheduleId || s.schedule_id,
                    startDatetime: s.startDatetime || s.start_datetime,
                    endDatetime: s.endDatetime || s.end_datetime,
                    isConfirmed: !!(s.isConfirmed || s.is_confirmed),
                }))
                .sort((a, b) => {
                    const ta = a.startDatetime ? new Date(a.startDatetime).getTime() : 0;
                    const tb = b.startDatetime ? new Date(b.startDatetime).getTime() : 0;
                    return tb - ta;
                });
            setAll(filtered);
        } catch {
            setAll([]);
        } finally {
            setIsLoading(false);
        }
    }, [meetingId]);

    useEffect(() => { load(); }, [load]);

    const preview = all.slice(0, 2);
    const hasMore = all.length >= 3;
    const totalPages = Math.max(1, Math.ceil(all.length / pageSize));
    const pageRows = useMemo(() => {
        const start = (page - 1) * pageSize;
        return all.slice(start, start + pageSize);
    }, [all, page, pageSize]);

    if (!isLoading && all.length === 0) return null;

    return (
        <>
            <div className={styles.wrap}>
                <div className={styles.head}>
                    <span className={styles.title}>{t('scheduleHistory.pastSessions')}</span>
                    <span className={styles.count}>
                        {t('scheduleHistory.countSessions', { count: all.length })}
                    </span>
                </div>
                <ul className={styles.list}>
                    {preview.map(s => (
                        <li key={s.scheduleId} className={styles.row}>
                            {formatRange(s.startDatetime, s.endDatetime)}
                        </li>
                    ))}
                </ul>
                {hasMore && (
                    <Button
                        variant="ghost"
                        block
                        onClick={() => { setPage(1); setShowAll(true); }}
                    >
                        {t('scheduleHistory.details')}
                    </Button>
                )}
            </div>

            <Sheet
                open={showAll}
                title={t('scheduleHistory.pastSessionsModal', { count: all.length })}
                closeLabel={t('common.close')}
                onClose={() => setShowAll(false)}
            >
                <ul className={styles.list}>
                    {pageRows.map(s => (
                        <li key={s.scheduleId} className={styles.row}>
                            {formatRange(s.startDatetime, s.endDatetime)}
                        </li>
                    ))}
                </ul>
                <div className={styles.pager}>
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                        {t('common.prev')}
                    </Button>
                    <span className={styles.pagerInfo}>{page} / {totalPages}</span>
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    >
                        {t('common.next')}
                    </Button>
                </div>
            </Sheet>
        </>
    );
};

export default ScheduleHistorySection;
